import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Receipt,
  RefreshCw,
  DollarSign,
  CreditCard,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  Bot,
  Play,
  Square,
  Settings,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import './PaymentLogs.css';

function PaymentLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0, failed: 0, totalAmount: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filter, setFilter] = useState('all');
  
  // TG Bot settings
  const [botToken, setBotToken] = useState('');
  const [botStatus, setBotStatus] = useState('stopped'); // stopped, running
  const [botLoading, setBotLoading] = useState(false);
  const [showBotSettings, setShowBotSettings] = useState(false);

  useEffect(() => {
    loadLogs();
    loadBotSettings();
  }, [page, filter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/payment-logs?page=${page}&limit=20&filter=${filter}`);
      setLogs(res.data.logs || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
      setStats(res.data.stats || { total: 0, completed: 0, pending: 0, failed: 0, totalAmount: 0 });
    } catch (error) {
      console.error('Load logs error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBotSettings = async () => {
    try {
      const res = await api.get('/api/admin/payment-notify-bot/settings');
      if (res.data) {
        setBotToken(res.data.token || '');
        setBotStatus(res.data.status || 'stopped');
      }
    } catch (error) {
      console.error('Load bot settings error:', error);
    }
  };

  const saveBotToken = async () => {
    setBotLoading(true);
    try {
      await api.post('/api/admin/payment-notify-bot/settings', { token: botToken });
      alert('Токен сохранён');
    } catch (error) {
      alert('Ошибка сохранения токена');
    } finally {
      setBotLoading(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm('Очистить всю историю оплаты? Записи будут скрыты из списка. Данные подписок и платежей в базе сохранятся.')) {
      return;
    }
    try {
      await api.post('/api/admin/payment-logs/clear');
      setPage(1);
      await loadLogs();
      alert('История оплаты очищена');
    } catch (error) {
      alert('Ошибка очистки: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleBot = async () => {
    setBotLoading(true);
    try {
      const action = botStatus === 'running' ? 'stop' : 'start';
      const res = await api.post(`/api/admin/payment-notify-bot/${action}`);
      setBotStatus(res.data.status || (action === 'start' ? 'running' : 'stopped'));
    } catch (error) {
      alert('Ошибка: ' + (error.response?.data?.error || error.message));
    } finally {
      setBotLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatAmount = (amount, currency) => {
    if (currency === 'RUB') return `${amount} ₽`;
    return `$${(amount / 100).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const map = {
      'completed': { label: 'Оплачено', cls: 'success', icon: CheckCircle },
      'succeeded': { label: 'Оплачено', cls: 'success', icon: CheckCircle },
      'pending': { label: 'Ожидание', cls: 'warning', icon: Clock },
      'failed': { label: 'Ошибка', cls: 'error', icon: XCircle },
      'canceled': { label: 'Отменён', cls: 'error', icon: XCircle },
    };
    const info = map[status] || { label: status, cls: 'default', icon: Clock };
    const Icon = info.icon;
    return (
      <span className={`status-badge ${info.cls}`}>
        <Icon size={14} />
        {info.label}
      </span>
    );
  };

  const getProviderBadge = (provider) => {
    const map = {
      'stripe': { label: 'Stripe', cls: 'stripe' },
      'yookassa': { label: 'ЮKassa', cls: 'yookassa' },
      'google_play': { label: 'Google Play', cls: 'googleplay' },
      'googleplay': { label: 'Google Play', cls: 'googleplay' },
      'apple': { label: 'Apple', cls: 'apple' },
      'apple_iap': { label: 'Apple', cls: 'apple' },
      'certificate': { label: 'Сертификат', cls: 'cert' },
      'trial': { label: 'Триал', cls: 'trial' },
    };
    const info = map[provider] || { label: provider, cls: 'default' };
    return <span className={`provider-badge ${info.cls}`}>{info.label}</span>;
  };

  if (loading && logs.length === 0) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка...</span>
      </div>
    );
  }

  return (
    <div className="payment-logs-page fade-in">
      <div className="page-header">
        <h1 className="page-title">История оплаты</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => setShowBotSettings(!showBotSettings)}>
            <Bot size={18} />
            TG Уведомления
          </button>
          <button className="btn btn-secondary" onClick={loadLogs}>
            <RefreshCw size={18} />
            Обновить
          </button>
          <button className="btn btn-danger" onClick={clearHistory}>
            <Trash2 size={18} />
            Очистить историю
          </button>
        </div>
      </div>

      {/* TG Bot Settings Panel */}
      {showBotSettings && (
        <div className="bot-settings-panel">
          <h3><Bot size={20} /> Telegram бот уведомлений</h3>
          <p className="bot-desc">Бот будет отправлять уведомления о новых оплатах всем подписчикам</p>
          
          <div className="bot-form">
            <div className="form-group">
              <label>Токен бота</label>
              <input
                type="text"
                className="input"
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
              />
            </div>
            
            <div className="bot-actions">
              <button className="btn btn-primary" onClick={saveBotToken} disabled={botLoading}>
                <Settings size={16} />
                Сохранить токен
              </button>
              
              <button 
                className={`btn ${botStatus === 'running' ? 'btn-danger' : 'btn-success'}`}
                onClick={toggleBot}
                disabled={botLoading || !botToken}
              >
                {botStatus === 'running' ? <Square size={16} /> : <Play size={16} />}
                {botStatus === 'running' ? 'Остановить' : 'Запустить'}
              </button>
              
              <span className={`bot-status ${botStatus}`}>
                {botStatus === 'running' ? '● Работает' : '○ Остановлен'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon total"><Receipt size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Всего платежей</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.completed}</span>
            <span className="stat-label">Успешных</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning"><Clock size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Ожидание</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon amount"><DollarSign size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">${(stats.totalAmount / 100).toFixed(0)}</span>
            <span className="stat-label">Общая сумма</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="tabs-container">
        {[
          { key: 'all', label: 'Все' },
          { key: 'completed', label: 'Успешные' },
          { key: 'pending', label: 'Ожидание' },
          { key: 'failed', label: 'Ошибки' },
        ].map(tab => (
          <button
            key={tab.key}
            className={`tab ${filter === tab.key ? 'active' : ''}`}
            onClick={() => { setFilter(tab.key); setPage(1); }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Logs Table */}
      <div className="logs-table-section">
        {logs.length === 0 ? (
          <div className="empty-state">
            <Receipt size={48} />
            <h3>Нет платежей</h3>
            <p>История платежей появится здесь</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Пользователь</th>
                    <th>Сумма</th>
                    <th>Провайдер</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={log.id || idx}>
                      <td>
                        <div className="date-cell">
                          <Calendar size={14} />
                          {formatDate(log.created_at || log.paid_at)}
                        </div>
                      </td>
                      <td>
                        <div className="user-cell">
                          <User size={14} />
                          <span>{log.user_name || log.user_email || log.user_id || '-'}</span>
                        </div>
                      </td>
                      <td>
                        <span className="amount">{formatAmount(log.amount, log.currency)}</span>
                      </td>
                      <td>{getProviderBadge(log.provider)}</td>
                      <td>{getStatusBadge(log.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="pagination-info">
                  {page} / {pagination.pages}
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentLogs;
